import React, { useState, useEffect, useContext } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./EventManagementDetails.css";
import StatCards from "../components/statCard/StatCard";
import Header from "../components/header/Header";
import DetailedEvent from "../components/detailedEvent/DetailedEvent";
import EventHeader from "../components/eventHeader/EventHeader";
import { AuthContext } from "../context/auth";
import {
  formatDateLessThanOneDay,
  formatDateWithOrdinal,
} from "../helper/utils";
import DigitalTicket from "../pages/digitalTicket/DigitalTicket";
import QRScanner from "../components/qrScanner/QRScanner";
import CheckInConfirmationModal from "../Modals/checkInConfirmationModal/CheckInConfirmationModal";
import { enrichInvitationsWithCheckIns } from "./enrichInvitationsWithCheckIns";
import EventDetailsSkeleton from "../components/skeletons/EventDetailsSkeleton";

const EventManagementDetails = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const { eventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const backPath = location.state?.from || "/explore";

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [event, setEvent] = useState(location.state?.eventDetails || null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const [showDigitalTicket, setShowDigitalTicket] = useState(false);
  const [ticketData, setTicketData] = useState(null);
  const [scannerVisible, setScannerVisible] = useState(false);
  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState(false);
  const [scannedGuestData, setScannedGuestData] = useState(null);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [refunds, setRefunds] = useState([]);

  const userRole = user?.activeRole || null;

  const hasActiveRole =
    userRole && ["host", "guest", "usher"].includes(userRole);
  const isUsher = userRole === "usher";
  const isHost = userRole === "host";
  const isGuest = userRole === "guest";

  const isUnauthenticatedUser = !isAuthenticated || !hasActiveRole;

  const loadEvent = async () => {
    setLoading(true);
    setError(null);

    try {
      const eventData = await fetchEvent(eventId);
      setEvent(eventData);
      computeStats(eventData);
    } catch (err) {
      setError(
        err.message || "Failed to load event details. Please try again."
      );

      if (location.state?.eventDetails) {
        const enrichedEvent = enrichInvitationsWithCheckIns(
          location.state.eventDetails
        );
        setEvent(enrichedEvent);
        computeStats(enrichedEvent);
        setError(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) {
      loadEvent();
    }
  }, [eventId]);

  useEffect(() => {
    if (location.state?.eventDetails && !event && !loading) {
      const enrichedEvent = enrichInvitationsWithCheckIns(
        location.state.eventDetails
      );
      setEvent(enrichedEvent);
      computeStats(enrichedEvent);
    }
  }, [location.state?.eventDetails, event, loading]);

  useEffect(() => {
    if (!user?._id || !isHost || !eventId) return;

    const fetchEventRefunds = async () => {
      try {
        const res = await axios.get(`/events/host/${user._id}/refund`);

        if (res.data.successStatus) {
          const allRefunds = res.data.refundRequests || [];
          const filtered = allRefunds.filter(
            (r) => String(r.eventDetails?.event_id) === String(eventId)
          );
          setRefunds(filtered);
        }
      } catch (err) {
        // console.error("Failed to fetch refunds", err);
      }
    };

    fetchEventRefunds();
  }, [user?._id, isHost, eventId]);

  useEffect(() => {
    if (!user?._id || !isHost || !eventId) return;

    const fetchEventRefunds = async () => {
      try {
        const res = await axios.get(`/events/host/${user._id}/refund`);

        if (res.data.successStatus) {
          const allRefunds = res.data.refundRequests || [];
          const filtered = allRefunds.filter(
            (r) => String(r.eventDetails?.event_id) === String(eventId)
          );
          setRefunds(filtered);
        }
      } catch (err) {
        // console.error("Failed to fetch refunds", err);
      }
    };

    fetchEventRefunds();
  }, [user?._id, isHost, eventId]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchEvent = async (eventId, attemptNumber = 0) => {
    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, attemptNumber), 5000);

    try {
      const token = localStorage.getItem("token");

      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(`/event/${eventId}`, {
        headers,
        timeout: 15000,
        withCredentials: false,
        validateStatus: (status) => {
          return status >= 200 && status < 500;
        },
      });

      if (
        response.status === 200 &&
        (response.data.successStatus || response.data.success)
      ) {
        const eventData = response.data.data || response.data.event;

        if (!eventData) {
          throw new Error("Event data not found in response");
        }

        const enrichedEvent = enrichInvitationsWithCheckIns({
          ...eventData,
          invitations:
            eventData.invitations ||
            eventData.guests ||
            eventData.attendees ||
            [],
          checkIns: eventData.checkIns || [],
          preferredCurrency: eventData.preferredCurrency || "NGN",
        });

        setRetryCount(0);
        return enrichedEvent;
      }

      if (response.status === 401) {
        try {
          const publicResponse = await axios.get(`/event/public/${eventId}`, {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            timeout: 15000,
            withCredentials: false,
          });

          if (
            publicResponse.data.successStatus ||
            publicResponse.data.success
          ) {
            const eventData =
              publicResponse.data.data || publicResponse.data.event;

            if (!eventData) {
              throw new Error("Event data not found in response");
            }

            const enrichedEvent = enrichInvitationsWithCheckIns({
              ...eventData,
              invitations: [],
              checkIns: [],
              preferredCurrency: eventData.preferredCurrency || "NGN",
            });

            setRetryCount(0);
            return enrichedEvent;
          }
        } catch (publicError) {
          console.error("Public endpoint also failed:", publicError);
        }
      }

      if (response.status === 404) {
        throw new Error(
          "Event not found. The event may have been deleted or the link is incorrect."
        );
      }

      if (response.status >= 400 && response.status < 500) {
        throw new Error(
          response.data?.message ||
            response.data?.error ||
            `Failed to load event (Error ${response.status})`
        );
      }

      throw new Error(response.data?.message || "Failed to fetch event");
    } catch (error) {
      const isAuthError =
        error.response?.status === 401 || error.response?.status === 403;
      const hasToken = !!localStorage.getItem("token");

      const shouldRetry =
        attemptNumber < maxRetries &&
        !isAuthError &&
        (!error.response ||
          error.response.status >= 500 ||
          error.code === "ECONNABORTED" ||
          error.code === "ERR_NETWORK");

      if (shouldRetry) {
        setRetryCount(attemptNumber + 1);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        return fetchEvent(eventId, attemptNumber + 1);
      }

      if (error.response) {
        switch (error.response.status) {
          case 404:
            throw new Error(
              "Event not found. The event may have been deleted or the link is incorrect."
            );
          case 401:
            if (!hasToken) {
              throw new Error(
                "This event requires authentication. Please log in to view details."
              );
            }
            throw new Error(
              "Session expired. Please log in again to view this event."
            );
          case 403:
            throw new Error(
              "Access denied. You don't have permission to view this event."
            );
          case 500:
          case 502:
          case 503:
          case 504:
            throw new Error("Server error. Please try again in a few moments.");
          default:
            throw new Error(
              error.response.data?.message ||
                error.response.data?.error ||
                `Failed to load event (Error ${error.response.status})`
            );
        }
      } else if (error.request) {
        throw new Error(
          "Network error. Please check your internet connection and try again."
        );
      } else {
        throw new Error(
          error.message || "An unexpected error occurred. Please try again."
        );
      }
    }
  };

  const computeStats = (eventData) => {
    if (
      !eventData ||
      !eventData.invitations ||
      !Array.isArray(eventData.invitations)
    ) {
      const emptyStats = {
        totalEvents: 0,
        totalGuests: 0,
        overallAmount: 0,
        eventAttireAmount: 0,
        ticketAmount: 0,
        cashGiftAmount: 0,
        refundRequests: 0,
        checkedInCount: 0,
      };
      setStats(emptyStats);
      return;
    }

    const uniqueGuestIds = new Set(
      eventData.invitations
        .filter((inv) => inv.guestId?._id)
        .map((inv) => inv.guestId._id)
    );

    const computedStats = {
      totalEvents: eventData ? 1 : 0,
      totalGuests: eventData.invitations.length,
      overallAmount: eventData.invitations.reduce(
        (sum, inv) => sum + (Number(inv.totalPaid) || 0),
        0
      ),

      eventAttireAmount: eventData.invitations.reduce(
        (sum, inv) =>
          sum +
          (inv.eventAttire
            ?.filter((attire) => attire.paid)
            ?.reduce(
              (acc, attire) => acc + (Number(attire.totalAmount) || 0),
              0
            ) || 0),
        0
      ),

      ticketAmount: eventData.invitations.reduce(
        (sum, inv) =>
          sum +
          (inv.ticketFeePaid
            ?.filter((ticket) => ticket.paid)
            ?.reduce(
              (acc, ticket) => acc + (Number(ticket.totalAmount) || 0),
              0
            ) || 0),
        0
      ),

      cashGiftAmount: eventData.invitations.reduce(
        (sum, inv) => sum + (Number(inv.cashGiftPaid?.giftAmountPaid) || 0),
        0
      ),

      refundRequests: eventData.invitations.filter(
        (inv) => inv.refundStatus === "pending"
      ).length,

      checkedInCount: eventData.invitations.filter(
        (inv) => inv.isCheckedIn === true
      ).length,
      usherCheckIns: eventData.checkIns?.length || 0,
    };

    setStats(computedStats);
  };

  const getEventStatus = (eventDate) => {
    if (!eventDate) return "Upcoming";

    const today = new Date();
    const event = new Date(eventDate);

    const todayDateOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const eventDateOnly = new Date(
      event.getFullYear(),
      event.getMonth(),
      event.getDate()
    );

    if (eventDateOnly > todayDateOnly) {
      return "Upcoming";
    } else if (eventDateOnly.getTime() === todayDateOnly.getTime()) {
      return "Live";
    } else {
      return "Complete";
    }
  };

  const guests =
    isAuthenticated && hasActiveRole && (isHost || isUsher || isGuest)
      ? event?.invitations?.map((inv) => {
          const guestData = {
            id: inv.guestId?._id || `guest-${Math.random()}`,
            name: `${inv.guestId?.firstName || "Guest"} ${
              inv.guestId?.lastName || ""
            }`.trim(),
            phone: inv.guestId?.phoneNumber || "N/A",
            email: inv.guestId?.email || "N/A",
            userPhoto: inv.guestId?.userPhoto || "/images/avatar.webp",
            rsvp: inv.status?.toLowerCase() === "accepted",
            eventAttire: inv.eventAttire?.find((attire) => attire.paid) || null,
            deliveryAddress:
              inv.eventAttire?.find((attire) => attire.deliveryAddress)
                ?.deliveryAddress || "N/A",
            ticket:
              inv.ticketFeePaid?.length > 0 ? inv.ticketFeePaid[0].type : "Nil",
            ticketFeePaid:
              inv.ticketFeePaid?.length > 0 ? inv.ticketFeePaid : [],
            cashGiftAmount: Number(inv.cashGiftPaid?.giftAmountPaid) || 0,
            totalPaid: Number(inv.totalPaid) || 0,
            rsvpStatus: inv.status?.toLowerCase() || "pending",
            responseDate: inv?.responseDate || null,
            isCheckedIn: inv.isCheckedIn || false,
            checkInTime: inv.checkInTime || null,
          };
          return guestData;
        }) || []
      : [];

  const ushers =
    isAuthenticated && hasActiveRole && (isHost || isUsher)
      ? event?.checkIns?.map((check) => {
          const usher = check.usherId || {};

          return {
            id: usher._id || `usher-${Math.random()}`,
            name: `${usher.firstName || "Usher"} ${
              usher.lastName || ""
            }`.trim(),
            phone: usher.phoneNumber || "N/A",
            email: usher.email || "N/A",
            userPhoto: usher.userPhoto || "/images/guestImage.png",
            timeIn: check.timeIn || "N/A",
            timeOut: check.timeOut || "N/A",
            checkInId: check._id,
          };
        }) || []
      : [];

  const others =
    isAuthenticated && hasActiveRole && (isHost || isGuest)
      ? event?.invitations?.map((inv) => {
          const guestData = {
            id: inv.guestId?._id || `guest-${Math.random()}`,
            name: `${inv.guestId?.firstName || "Guest"} ${
              inv.guestId?.lastName || ""
            }`.trim(),
            phone: inv.guestId?.phoneNumber || "N/A",
            email: inv.guestId?.email || "N/A",
            userPhoto: inv.guestId?.userPhoto || "/images/avatar.webp",
            rsvp: inv.status?.toLowerCase() === "accepted",
            gift: inv.cashGiftPaid?.giftAmountPaid
              ? `₦${Number(inv.cashGiftPaid.giftAmountPaid).toLocaleString(
                  "en-NG"
                )}`
              : "₦0",
            aso: inv.eventAttire?.some((attire) => attire.paid)
              ? "Aso Ebi"
              : "",
            eventAttire: inv.eventAttire?.find((attire) => attire.paid) || null,
            eventAttireImage:
              inv.eventAttire?.find((attire) => attire.paid)?.uri ||
              "/images/default-attire.jpg",
            deliveryAddress:
              inv.eventAttire?.find((attire) => attire.deliveryAddress)
                ?.deliveryAddress || "N/A",
            ticket:
              inv.ticketFeePaid?.length > 0 ? inv.ticketFeePaid[0].type : "Nil",
            ticketFeePaid:
              inv.ticketFeePaid?.length > 0 ? inv.ticketFeePaid : [],
            cashGiftAmount: Number(inv.cashGiftPaid?.giftAmountPaid) || 0,
            totalPaid: Number(inv.totalPaid) || 0,
            rsvpStatus: inv.status?.toLowerCase() || "pending",
          };
          return guestData;
        }) || []
      : [];

  const handleShowDigitalTicket = () => {
    if (!canGenerateTicket()) {
      alert("You must complete all required purchases to generate a ticket.");
      return;
    }

    const userInvitation = event?.invitations?.find(
      (invite) => invite.guestId?._id === user?._id
    );

    const ticketType =
      userInvitation?.ticketFeePaid?.[0]?.type || "General Admission";

    setTicketData({
      guestName: `${user?.firstName} ${user?.lastName}`,
      guestId: user?._id,
      eventId: event?._id,
      eventTitle: event?.eventTitle,
      eventDate: event?.eventDate || event?.startDate,
      eventTime: event?.eventTime || event?.startTime,
      eventLocation: event?.eventLandmark || event?.eventLocation,
      ticketType,
      isCheckedIn: userInvitation?.isCheckedIn || false,
    });
    setShowDigitalTicket(true);
  };

  const canGenerateTicket = () => {
    const userInvitation = event?.invitations?.find(
      (invite) => invite.guestId?._id === user?._id
    );

    if (!userInvitation) return false;

    const invitationStatus = userInvitation.status?.toLowerCase();
    if (invitationStatus !== "accepted") return false;

    const hasRequiredAttire = event?.isEventAttireCompulsory
      ? userInvitation.eventAttire?.some((attire) => attire.paid)
      : true;

    const hasRequiredTicket = event?.isEntryFeeApplicable
      ? userInvitation.ticketFeePaid?.some((ticket) => ticket.paid)
      : true;

    return hasRequiredAttire && hasRequiredTicket;
  };

  const handleOpenScanner = () => {
    setScannerVisible(true);
  };

  const handleScanSuccess = (qrData) => {
    setScannerVisible(false);
    setScannedGuestData(qrData);
    setConfirmationModalVisible(true);
  };

  const handleConfirmCheckIn = async (guestData) => {
    setIsCheckingIn(true);
    try {
      const response = await axios.post(
        `/event/${eventId}/checkin`,
        {
          guestId: guestData.guestId,
          usherId: user?._id,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (response.data.successStatus) {
        alert("Guest checked in successfully!");
        setConfirmationModalVisible(false);
        setScannedGuestData(null);

        // DEV MERGE: Use refreshing state for skeleton display
        setRefreshing(true);
        const updatedEvent = await fetchEvent(eventId);
        setEvent(updatedEvent);
        computeStats(updatedEvent);
        setRefreshing(false);
      } else {
        alert(response.data.message || "Check-in failed");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "Failed to check in guest";
      alert(errorMsg);
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleRetry = async () => {
    setRetryCount(0);
    await loadEvent();
  };

  if (loading && !event) {
    return (
      <div className="event-management-details-eventManagement">
        <Header />
        <div className="event-management-details-loading-container">
          <div className="event-management-details-spinner"></div>
          <p>Loading event details...</p>
          {retryCount > 0 && (
            <p
              style={{ fontSize: "14px", color: "#666", marginTop: "10px" }}
            ></p>
          )}
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="event-management-details-eventManagement">
        <Header />
        <div className="event-management-details-error-container">
          <svg
            width="80"
            height="80"
            viewBox="0 0 24 24"
            fill="none"
            style={{ marginBottom: "20px" }}
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
              fill="#d32f2f"
            />
          </svg>
          <h2>Unable to Load Event</h2>
          <p className="event-management-details-error-message">{error}</p>
          <div className="event-management-details-error-actions">
            <button
              onClick={handleRetry}
              className="event-management-details-retry-button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
                  fill="currentColor"
                />
              </svg>
              Try Again
            </button>
            <button
              onClick={() => navigate(backPath)}
              className="event-management-details-back-button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
                  fill="currentColor"
                />
              </svg>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-management-details-eventManagement">
        <Header />
        <div className="event-management-details-not-found-container">
          <h2>Event Not Found</h2>
          <p>The event you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate(backPath)}
            className="event-management-details-back-button"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="event-management-details-eventManagement">
      <Header />

      <div className="event-management-details-eventManagement-header">
        <EventHeader
          title={event?.eventTitle || event?.eventName || "Untitled Event"}
          status={
            event
              ? getEventStatus(event.eventDate || event.startDate)
              : "Upcoming"
          }
          date={
            event
              ? formatDateLessThanOneDay(
                  event.eventDate ||
                    event.startDate ||
                    event.multipleDates?.[0]?.date
                )
              : ""
          }
          backPath={backPath}
          isUsher={isUsher}
          onScanClick={handleOpenScanner}
          isGuest={isGuest}
          canGenerateTicket={canGenerateTicket()}
          onTicketClick={handleShowDigitalTicket}
        />
      </div>

      <div className="event-management-details-events-section">
        {loading || refreshing ? (
          <EventDetailsSkeleton />
        ) : (
          <div className="event-management-details-events-list">
            <DetailedEvent
              event={event}
              ushers={ushers}
              eventId={eventId}
              guests={guests}
              refunds={refunds}
              setRefunds={setRefunds}
              others={others}
              userRole={userRole}
              isAuthenticated={isAuthenticated}
              isUsher={isUsher}
              isHost={isHost}
              isGuest={isGuest}
              isUnauthenticatedUser={isUnauthenticatedUser}
              onRefresh={async () => {
                setRefreshing(true);
                const updatedEvent = await fetchEvent(eventId);
                setEvent(updatedEvent);
                computeStats(updatedEvent);
                setRefreshing(false);
              }}
            />
          </div>
        )}
      </div>

      {showDigitalTicket && ticketData && (
        <DigitalTicket
          isOpen={showDigitalTicket}
          onClose={() => setShowDigitalTicket(false)}
          ticketData={ticketData}
          event={event}
        />
      )}

      {scannerVisible && (
        <QRScanner
          isOpen={scannerVisible}
          onClose={() => setScannerVisible(false)}
          onScanSuccess={handleScanSuccess}
          eventId={eventId}
        />
      )}

      {confirmationModalVisible && scannedGuestData && (
        <CheckInConfirmationModal
          isOpen={confirmationModalVisible}
          onClose={() => {
            setConfirmationModalVisible(false);
            setScannedGuestData(null);
          }}
          onConfirm={handleConfirmCheckIn}
          guestData={scannedGuestData}
          isLoading={isCheckingIn}
        />
      )}
    </div>
  );
};

export default EventManagementDetails;
